<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import type { StripeEmbeddedCheckout } from '@stripe/stripe-js'
import * as Sentry from '@sentry/browser'
import { stripeModule } from '@/store/stripe/module'
import { upgradeModule } from '@/store/upgrade/module'

const props = withDefaults(defineProps<{
    mode: 'subscription' | 'payment' | 'setup'
    priceId?: string
    referralId?: string
    impactClickId?: string
}>(), {
    priceId: undefined,
    referralId: undefined,
    impactClickId: undefined,
})

const emit = defineEmits<{
    'checkoutCompleted': [checkoutSessionId: string]
    'error': [error: unknown]
}>()

const checkout = ref<StripeEmbeddedCheckout | null>(null)
const checkoutSessionId = ref<string | null>(null)

const paymentPlatform = computed(() => {
    return upgradeModule.getters.getPaymentPlatform()
})

onMounted(() => {
    nextTick(async () => {
        try {
            if (import.meta.env.VUE_APP_STRIPE_PUBLISHABLE_KEY) {
                const { stripe } = await stripeModule.actions.loadStripeState()

                const {
                    clientSecret,
                    checkoutSessionId: newCheckoutSessionId,
                } = await stripeModule.actions.createCheckoutSession({
                    mode: props.mode,
                    priceId: props.priceId,
                    referralId: props.referralId,
                    impactClickId: props.impactClickId,
                    paymentPlatform: paymentPlatform.value,
                })

                if (stripe && clientSecret && newCheckoutSessionId) {
                    checkoutSessionId.value = newCheckoutSessionId
                    checkout.value = await stripe.initEmbeddedCheckout({
                        clientSecret,
                        onComplete: () => {
                            emitCheckoutCompleted()
                        },
                    })

                    checkout.value.mount('#checkout')
                }
            }
        } catch (err) {
            Sentry.captureException(err)
            emit('error', err)
        }
    })
})

onBeforeUnmount(() => {
    if (checkout.value) {
        checkout.value.destroy()
    }
})

const emitCheckoutCompleted = () => {
    if (checkout.value && checkoutSessionId.value) {
        emit('checkoutCompleted', checkoutSessionId.value)
    }
}
</script>

<template>
    <div class="payment-fields">
        <div id="checkout" />
    </div>
</template>

<style lang="scss" scoped>
.payment-fields {
    position: relative;
    width: 100%;
    height: 100%;
}
</style>
